/**
 * 챌린지 속성(`CHALLENGE_ATTR_CN`) — 종류(쿼리/튜닝)·DBMS 를 챌린지에 **적어 둔** JSON 한 칸(2026-10-05).
 *
 *   {"v":1,"kind":"query"|"tuning","dbms":"oracle"|"postgresql", …}
 *
 * 저장 위치: QBase tb0074.gc008(CHALLENGE_ATTR_CN, 뷰 vw_qry_challenge.challenge_attr_cn) · 로컬 chal_challenge.challenge_attr_cn.
 * 서버 쪽 같은 정규화는 backend/modules/challenge/challengeAttr.js.
 *
 * 판정 순서 — **적어 둔 값이 먼저**, 없을 때만 문항으로 추론한다(속성이 아직 없는 QBase 챌린지·예전 교환 파일).
 * 추론은 「튜닝 문항(003·004)이 있으면 튜닝」 이라, 다지선택만 든 튜닝 챌린지(QBase QC00007)는 속성을 적어야 바로 선다.
 */

export const CHALLENGE_KINDS = ['query', 'tuning'];
export const CHALLENGE_DBMS = ['oracle', 'postgresql'];

/** 튜닝 문항 유형 — 실습(SQL 개선·인덱스 설계) */
export const TUNING_QUIZ_TCDS = ['003', '004'];
export const hasTuningQuiz = (quizzes) => Array.isArray(quizzes)
  && quizzes.some(q => TUNING_QUIZ_TCDS.includes(String(q.QUIZ_TCD || q.quizTcd || '')));

/** 문자열·객체·없음을 객체로 — 깨진 값은 빈 객체(화면이 죽지 않게) */
export function parseChallengeAttr(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return { ...raw };
  try {
    const v = JSON.parse(String(raw));
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
  } catch (_e) {
    return {};
  }
}

/** 챌린지 행(리덕스 UPPER 또는 camelCase)의 속성. 예전 DBMS 칸(DBMS/challengeDbms)도 받아 준다 */
export function attrOf(ch) {
  if (!ch) return {};
  const a = parseChallengeAttr(ch.CHALLENGE_ATTR_CN ?? ch.challengeAttrCn);
  const legacyDbms = ch.DBMS || ch.challengeDbms;
  if (!a.dbms && legacyDbms) a.dbms = String(legacyDbms).toLowerCase();
  return a;
}

/**
 * 챌린지 종류 — 'query' | 'tuning' | null(아직 모름: 속성이 없고 문항도 안 읽었다).
 * @param quizzes 그 챌린지의 문항 목록(읽었으면). undefined 면 아직 안 읽은 것
 */
export function kindOf(ch, quizzes) {
  const k = attrOf(ch).kind;
  if (CHALLENGE_KINDS.includes(k)) return k;
  if (quizzes === undefined) return null;
  return hasTuningQuiz(quizzes) ? 'tuning' : 'query';
}

/**
 * 작성중인가 — 속성의 `status: "draft"`(2026-10-06). 웹에서는 작성자(mgrUsrid)에게만 보인다.
 * 로컬 저장소는 백엔드(challengeVisibility.js)가 숨기고, 화면은 같은 판정으로 표시·필터만 한다.
 */
export const isDraftChallenge = (ch) => attrOf(ch).status === 'draft';

/** 챌린지 DBMS — 적어 둔 것이 없으면 Oracle(예전 챌린지는 전부 Oracle 이다) */
export function dbmsOf(ch) {
  const d = attrOf(ch).dbms;
  return CHALLENGE_DBMS.includes(d) ? d : 'oracle';
}

/** 내보낼 때 — 적어 둔 것이 없으면 문항으로 추론한 종류라도 적어 준다(받는 쪽이 다시 추론하지 않게) */
export function attrForExport(ch, quizzes) {
  const a = attrOf(ch);
  if (!CHALLENGE_KINDS.includes(a.kind)) {
    const k = kindOf(ch, quizzes);
    if (k) a.kind = k;
  }
  if (!Object.keys(a).length) return null;
  if (a.v == null) a.v = 1;
  return JSON.stringify(a);
}
